import dayjs from 'dayjs';
import apiSaga from 'legacy/src/shared/util/apiSagaGenerator';
import { takeLatest, all, put, select } from 'redux-saga/effects';
import omit from 'lodash/omit';
import groupBy from 'lodash/groupBy';
import find from 'lodash/find';
import { SubmissionError } from 'redux-form';
import { assignAssessmentRoutine, resetAssignmentState } from './actions';
import { getEducationPeriodForSubject } from '../../../auth/utils';
import { getPart2ExamDuration } from 'chameleon-src/components/assign/AssignManageForm/utils/constants';
import { convertKeysToSnakeCase } from 'legacy/src/components/util';

const getScrambledQuestionParams = (scrambleQuestionsValue) => {
  const params = {
    scrambleQuestionsPerStudent: false,
    scrambleQuestions: false,
  };
  if (scrambleQuestionsValue === 'scramble_questions_per_student') {
    params.scrambleQuestionsPerStudent = true;
  } else {
    params.scrambleQuestions = !!scrambleQuestionsValue;
  }
  return params;
};

/**
 * Assign saga.
 * Create a new api saga for assigning an assessment.
 *
 * @param {Object} action.payload Data object.
 * @yield {apiSaga}               Api Response.
 */
function* assignSaga(action) {
  const { payload } = action;
  const {
    props: {
      itemId,
      subject,
      userData,
      individualStudentAssignment,
      isFinalExam,
      isPilot,
      allAssigneesStudents,
      individualStudentAssignmentFlag,
    },
    values,
  } = payload;
  const withIndividualStudentAssignment =
    individualStudentAssignment || individualStudentAssignmentFlag;
  const {
    allAssignees,
    assignees: assigneesValues,
    assessmentGroup,
    partType,
    students: formStudents = [],
    updateAssignees,
  } = values;

  /*
   * Our student.id field has STUDENT_ID__SECTION_ID__ASSIGNMENT_ID structure.
   * This is used for separating students within the assign/manage form based on different ISAs.
   * Therefore we need to set back the student.id field to STUDENT_ID (student.studentId property)
   * before sending the data the the backend.
   * */
  const students = formStudents.map((student) => ({
    ...student,
    id: student.studentId,
  }));

  const getStudentIds = (sectionId) =>
    students
      .filter(
        (student) =>
          parseInt(student.sectionId, 10) === parseInt(sectionId, 10),
      )
      .map((student) => student.id) || [];
  const groupStudentsBySectionId = groupBy(students, 'sectionId');

  const allAssigneeIds = allAssignees.map((assignee) => assignee.sectionId);

  let assignees = assigneesValues.map((a) => {
    const sectionId = a.sectionId.toString();
    const assigneeStudentIds = getStudentIds(a.sectionId);
    const isAssigneeSelected = allAssigneeIds.indexOf(sectionId) >= 0;
    const assigneeAllStudents = allAssigneesStudents.filter(
      (student) => student.sectionId === sectionId,
    );
    const isISA = assigneeStudentIds.length < assigneeAllStudents.length;

    return {
      id: String(a.sectionId),
      type: 'group',
      student_ids: isAssigneeSelected && !isISA ? [] : assigneeStudentIds,
    };
  });

  const { classes: subjectClassList = [] } = subject || {};

  if (withIndividualStudentAssignment && !isFinalExam) {
    Object.keys(groupStudentsBySectionId).forEach((sectionId) => {
      // check if this is a full class assignment
      const { students: studentsInGroup = [] } =
        find(subjectClassList, ['id', String(sectionId)]) || {};
      const isFullClass =
        studentsInGroup.length === groupStudentsBySectionId[sectionId].length;
      const assigneeIds = assignees.map((a) => a.id);
      const updateAssigneeIds = updateAssignees.map(
        (assignee) => assignee.sectionId,
      );
      if (
        !assigneeIds.includes(sectionId) &&
        !updateAssigneeIds.includes(sectionId)
      ) {
        assignees.push({
          id: String(sectionId),
          type: 'group',
          student_ids: isFullClass
            ? []
            : groupStudentsBySectionId[sectionId].map((student) => student.id),
        });
      }
    });
  }

  // If timer is zero then we set it to null to mark the assessment as non-timed
  if (parseInt(values.timer, 10) === 0) {
    values.timer = null;
  }

  let itemIds;
  let assignableGroupMembers;
  if (assessmentGroup) {
    assignableGroupMembers = assessmentGroup.members;
    /**
     * PREAP assessment taken in multiple sessions
     * */
    itemIds = assignableGroupMembers
      .filter((m) => m.partType === partType.value)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((m) => m.assessmentId);
  } else {
    itemIds = [itemId];
  }

  const addPreTestQuestions = true;
  let pretestQuestionsAdded = false;

  const { id: subjectId, isPreApSubject } = subject;

  // NOTE: It is entirely possible for an assignment to fail to assign in the middle of these.
  // TODO: Update the assign call in service-fym to take an array of assessment ids rather than just one.
  const itemIdsLength = itemIds.length;

  let requests = [];
  for (let i = 0; i < itemIdsLength; i++) {
    let currentItemId = itemIds[i];

    let mutatedValues = { ...omit(values, ['updateAssignees']) };
    if (isPreApSubject && isPilot) {
      // For PreAP pilot items, we don't allow students to see scores, ever.
      // So, hard-set the score_display value to false before we send it to the API.
      mutatedValues.score_display = false;
    }
    if (mutatedValues.startsAt && mutatedValues.startsAt instanceof dayjs) {
      mutatedValues.startsAt = mutatedValues.startsAt.format();
    }
    if (mutatedValues.dueAt && mutatedValues.dueAt instanceof dayjs) {
      mutatedValues.dueAt = mutatedValues.dueAt.format();
    }
    if (i > 0 && mutatedValues && mutatedValues.startsAtPart2) {
      // in the event that there were multiple parts, use the second date in the form
      mutatedValues.startsAt = mutatedValues.startsAtPart2.format();
      if (mutatedValues.duration) {
        mutatedValues.timer = getPart2ExamDuration(isPilot);
        mutatedValues.duration = getPart2ExamDuration(isPilot);
      }
    }

    if (assessmentGroup) {
      // since this is a final exam, we micromanage the name of the assessment:
      let partName = '';
      if (itemIds.length > 1) {
        partName = ` Part ${i + 1}`;
      }
      mutatedValues.final_exam_title = `${assessmentGroup.name}${partName}`;
    }

    const educationPeriod = getEducationPeriodForSubject({
      subjectId,
      userData,
    });

    const hasImmediateFeedbackFF = yield select(
      (state) => state.featureFlag.immediate_feedback,
    );

    const updatedHasImmediateFeedback =
      hasImmediateFeedbackFF && mutatedValues.hasImmediateFeedback;

    const data = convertKeysToSnakeCase({
      ...omit(mutatedValues, [
        'students',
        'hasScrambledOrder',
        'immediateFeedbackAttempts',
        'hasImmediateFeedback',
      ]),
      ...getScrambledQuestionParams(mutatedValues.hasScrambledOrder),
      hasImmediateFeedback: updatedHasImmediateFeedback,
      ...(updatedHasImmediateFeedback && {
        immediateFeedbackAttempts: Number(
          mutatedValues.immediateFeedbackAttempts,
        ),
      }),
      assignees,
      item: { id: currentItemId },
      add_pretest_questions: addPreTestQuestions && !pretestQuestionsAdded,
      educationPeriod,
    });

    if (assignees.length) {
      requests.push({
        method: 'post',
        url: 'assessments/api/assignments/',
        data,
      });
    }

    pretestQuestionsAdded = true;

    //used for preassigned PPCs
    if (updateAssignees && updateAssignees.length) {
      // TODO confirm this when doing the update assign functionality
      const updateAssigneesToSubmit = updateAssignees.map((a) => ({
        id: String(a.sectionId),
        type: 'group',
        assignmentId: String(a.assignmentId),
        student_ids:
          allAssigneeIds.indexOf(a.sectionId) >= 0
            ? []
            : getStudentIds(a.sectionId),
      }));
      try {
        requests.push({
          method: 'post',
          url: 'assessments/api/assignments/update/',
          data: convertKeysToSnakeCase({
            ...omit(mutatedValues, ['students']),
            assignees: updateAssigneesToSubmit,
          }),
        });
      } catch (errors) {
        throw new SubmissionError(errors.error);
      }
    }
  }

  try {
    /*
     * for Preap pilot assignment with 2 sessions, the requests should be sequential.
     * for all other cases, it can be concurrent requests.
     */
    yield apiSaga(assignAssessmentRoutine, requests, (x) => x, true, !isPilot);
  } catch (errors) {
    throw new SubmissionError(errors.error);
  }

  yield put(resetAssignmentState());
}

/**
 * AssignItem Saga
 *
 * @yield {Saga} All sagas that belong to assigning an assessment to classes.
 */
export default function* assessmentSaga() {
  yield all([takeLatest(assignAssessmentRoutine.TRIGGER, assignSaga)]);
}
